/** One chat with the user's workspace, wired to the page: what it sends carries the page; what comes back acts on it. */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { startWorkspace, useHermesChat, workspaceId, type HermesChat } from '@softmax/ide/workspace-client'
import { runPageAction, type ActionResult } from './actions'
import {
  addLearned,
  buildClientContext,
  buildInstructions,
  forgetLearned,
  isPrivatePage,
  onCogentNameChange,
  readCogentName,
  readLearned,
  type CogentMode,
  type PageFacts,
} from './preprompt'
import { parseReply, type PageAction } from './protocol'
import type { CogentSite } from './site'
import type { CogentView } from './ui/view/View'

export type CogentChatMessage = {
  id: string
  from: 'you' | 'cogent'
  text: string
  views: CogentView[]
  /** Waiting for the workspace to start on it. */
  queued: boolean
}

/** One question at a time: everything the cogent asks waits its turn. */
export type Confirmation = {
  question: string
  yes: string
  answer: (yes: boolean) => void
}

export type CogentSession = {
  chat: HermesChat
  messages: CogentChatMessage[]
  /** The reply as it streams in, without any block it is still writing. */
  draft: string | null
  /** What the reply has shown so far. */
  draftViews: CogentView[]
  /** A press that waits for the viewer's yes. */
  confirmation: Confirmation | null
  /** Not open, but still following work in progress, so the host can say what the cogent is doing. It can't act. */
  following: boolean
  /** What the user has approved the cogent keeping; they can remove any of it. */
  lessons: string[]
  forgetLesson: (lesson: string) => void
  send: (text: string) => Promise<void>
}

const ANSWER_AFTER_MS = 350

const textOf = (message: HermesChat['data']['messages'][number]) =>
  message.parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('\n')

/** The name this viewer gave their cogent on this site, once they have. */
export function useCogentName(siteId: string, viewerId: string | null) {
  return useSyncExternalStore(
    onCogentNameChange,
    () => (viewerId ? readCogentName(siteId, viewerId) : null),
    () => null
  )
}

export function useCogentSession({
  site,
  page,
  enabled,
  sessionKey,
  viewerId,
  leagueId,
  coworldId,
  mode,
  followFromStart = false,
}: {
  site: CogentSite
  page: PageFacts
  /** The user has it open. Opening wakes the workspace; a chat nobody opened stays asleep. */
  enabled: boolean
  /** The user started something a moment ago on another page: look for it without being opened. */
  followFromStart?: boolean
  sessionKey: string | null
  viewerId: string | null
  leagueId: string
  coworldId: string | null
  mode?: CogentMode
}): CogentSession {
  const [following, setFollowing] = useState(followFromStart)
  const chat = useHermesChat({
    enabled: enabled || following,
    clientId: site.id,
    sessionKey,
    scope: coworldId
      ? { scope: 'coworld', coworld: coworldId, league: leagueId }
      : { scope: 'workspace', coworld: null, league: null },
    prepareWorkspace: async (signal) => {
      await startWorkspace(signal)
      return workspaceId()
    },
    onSessionChange: () => {},
    onEvent: () => {},
  })
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const decline = useRef<(() => void) | null>(null)
  // A chat handed over from another page may act until the user has opened and closed it themselves.
  const closed = useRef(false)
  const wasOpen = useRef(false)
  // Work queued before a close stays dropped when the chat is reopened.
  const closes = useRef(0)
  useEffect(() => {
    closed.current = !enabled && wasOpen.current

    if (closed.current) {
      closes.current++
      decline.current?.()
    }

    wasOpen.current = enabled
  }, [enabled])
  const [lessons, setLessons] = useState<string[]>([])
  const results = useRef<ActionResult[]>([])
  const problems = useRef<string[]>([])
  // Replies this page has already acted on. History is never acted on: only a reply that arrives while it is open.
  const acted = useRef<Set<string> | null>(null)
  // A reply already running when the page opened may have acted before the reload, so it is left alone.
  const catchingUp = useRef(false)
  // How many actions each stream has run, and what its transcript copy must therefore not run again.
  const streamed = useRef(new Map<string, number>())
  const pendingFromStream = useRef<string[]>([])
  const problemsFromStream = useRef<string[]>([])

  // A question nobody can see would hold up everything after it, so it counts as work too.
  // Not chat.status: opening the chat also reads as resuming, and closing must cancel that read.
  const working =
    chat.control.sending || chat.control.active || chat.control.nativeApproval !== undefined || confirmation !== null
  const { loaded } = chat.control
  useEffect(() => {
    if (loaded) setFollowing(working)
  }, [loaded, working])

  const raw = chat.data.messages
  const finished = useMemo(
    () =>
      raw.filter(
        (message) =>
          message.role === 'assistant' && message.metadata?.status !== 'streaming' && textOf(message).length > 0
      ),
    [raw]
  )
  const streaming = raw.find((message) => message.metadata?.status === 'streaming')
  const streamingText = streaming ? textOf(streaming) : null
  const streamingId = streaming?.id ?? null
  const active = chat.control.active

  useEffect(() => {
    setLessons(viewerId ? readLearned(site.id, viewerId) : [])
  }, [site.id, viewerId])

  const queue = useRef(Promise.resolve())
  const lessonsAsked = useRef(new Set<string>())
  const ask = useCallback(
    (question: string, yes: string) =>
      new Promise<boolean>((resolve) => {
        if (closed.current) return resolve(false)

        const asked = performance.now()
        const settle = (answer: boolean) => {
          decline.current = null
          setConfirmation(null)
          resolve(answer)
        }
        decline.current = () => settle(false)
        setConfirmation({
          question,
          yes,
          answer: (answer) => {
            // A click already on its way when the question appeared is not an answer to it.
            if (performance.now() - asked >= ANSWER_AFTER_MS) settle(answer)
          },
        })
      }),
    []
  )
  const act = useCallback(
    (actions: PageAction[]) => {
      const queuedAt = closes.current

      // Refs name elements on this page: whatever follows a go is for a page that isn't here yet.
      const go = actions.findIndex(({ kind }) => kind === 'go')
      const here = go === -1 ? actions : actions.slice(0, go + 1)

      for (const action of here)
        queue.current = queue.current.then(async () => {
          // One action that throws must not silently stop every action after it.
          const result = await runPageAction(action, {
            confirm: (question) => ask(question, 'Yes, press it'),
            privatePage: isPrivatePage(site),
            closed: () => closed.current || closes.current !== queuedAt,
          }).catch((error: unknown) => {
            console.error('Cogent: a page action failed.', error)
            return { kind: action.kind, ref: action.ref, outcome: 'missing' as const, label: '' }
          })
          results.current.push(result)
        })
    },
    [ask, site]
  )
  const learn = useCallback(
    (lessons: string[]) => {
      if (!viewerId || closed.current) return

      for (const lesson of lessons) {
        if (lessonsAsked.current.has(lesson)) continue

        lessonsAsked.current.add(lesson)
        const queuedAt = closes.current
        queue.current = queue.current.then(async () => {
          if (closes.current === queuedAt && (await ask(`Remember this for next time? "${lesson}"`, 'Remember it')))
            setLessons(addLearned(site.id, viewerId, lesson))
        })
      }
    },
    [ask, site.id, viewerId]
  )

  useEffect(() => {
    if (!chat.control.loaded) return

    if (acted.current === null) {
      acted.current = new Set(finished.map((message) => message.id))
      catchingUp.current = active
      return
    }

    if (catchingUp.current) {
      for (const message of finished) acted.current.add(message.id)

      if (!active) catchingUp.current = false

      return
    }

    // As soon as a block closes in the stream, so pointing at something feels immediate.
    if (streamingId && streamingText) {
      const reply = parseReply(streamingText)
      const fresh = reply.actions.slice(streamed.current.get(streamingId) ?? 0)

      learn(reply.remember)

      for (const problem of reply.problems)
        if (!problemsFromStream.current.includes(problem)) {
          problemsFromStream.current.push(problem)
          problems.current.push(problem)
        }

      if (fresh.length) {
        streamed.current.set(streamingId, reply.actions.length)
        pendingFromStream.current.push(...fresh.map((action) => JSON.stringify(action)))
        act(fresh)
      }
    }

    for (const message of finished) {
      if (acted.current.has(message.id)) continue

      acted.current.add(message.id)
      const reply = parseReply(textOf(message))
      for (const problem of reply.problems) {
        const at = problemsFromStream.current.indexOf(problem)

        if (at === -1) problems.current.push(problem)
        else problemsFromStream.current.splice(at, 1)
      }

      learn(reply.remember)

      act(
        reply.actions.filter((action) => {
          const at = pendingFromStream.current.indexOf(JSON.stringify(action))

          if (at === -1) return true

          pendingFromStream.current.splice(at, 1)
          return false
        })
      )
    }
  }, [finished, chat.control.loaded, site.id, streamingId, streamingText, active, act, learn])

  const forgetLesson = useCallback(
    (lesson: string) => {
      if (viewerId) setLessons(forgetLearned(site.id, viewerId, lesson))
    },
    [site.id, viewerId]
  )

  const { send: sendMessage } = chat
  const send = useCallback(
    async (text: string) => {
      const context = buildClientContext({
        site,
        page,
        mode,
        results: results.current,
        problems: problems.current,
        lessons: viewerId ? readLearned(site.id, viewerId) : [],
        cogentName: viewerId ? readCogentName(site.id, viewerId) : null,
      })
      results.current = []
      problems.current = []
      await sendMessage(
        { role: 'user', content: text },
        {
          clientContext: context,
          instructions: buildInstructions(site),
        }
      )
    },
    [sendMessage, site, page, mode, viewerId]
  )

  const messages = useMemo<CogentChatMessage[]>(
    () =>
      raw
        .filter((message) => message.metadata?.status !== 'streaming')
        .map((message) => {
          const reply =
            message.role === 'assistant' ? parseReply(textOf(message)) : { text: textOf(message), views: [] }
          return {
            id: message.id,
            from: message.role === 'user' ? ('you' as const) : ('cogent' as const),
            text: reply.text,
            views: reply.views,
            queued: message.metadata?.status === 'queued',
          }
        })
        .filter((message) => message.text.length > 0 || message.views.length > 0),
    [raw]
  )
  const draft = useMemo(() => (streamingText === null ? null : parseReply(streamingText)), [streamingText])

  return {
    chat,
    messages,
    draft: draft?.text ?? null,
    draftViews: draft?.views ?? [],
    confirmation,
    following,
    lessons,
    forgetLesson,
    send,
  }
}
